import { useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { History, Clock, User, RotateCcw, Eye, X, AlertTriangle, ArrowLeft, GitCompare } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useVersions, useRevertVersion } from "@/hooks/useVersions";
import { useArticle } from "@/hooks/useKBData";
import { cn } from "@/lib/utils";
import type { ArticleVersion } from "@/types/api";

interface VersionHistoryDrawerProps {
  articleSlug: string;
  currentVersion?: number;
  trigger?: React.ReactNode;
}

export function VersionHistoryDrawer({
  articleSlug,
  currentVersion,
  trigger
}: VersionHistoryDrawerProps) {
  const [open, setOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<ArticleVersion | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertSummary, setRevertSummary] = useState("");

  const { data: versionsData, isLoading } = useVersions(articleSlug);
  const { data: articleData } = useArticle(articleSlug);
  const revertMutation = useRevertVersion();

  const versions = versionsData?.versions || [];

  const handleVersionSelect = (version: ArticleVersion) => {
    setSelectedVersion(version);
    setShowDetails(true);
    setShowComparison(false);
  };

  const handleCompareWithCurrent = (version: ArticleVersion) => {
    setSelectedVersion(version);
    setShowDetails(true);
    setShowComparison(true);
  };

  const handleBackToList = () => {
    setShowDetails(false);
    setShowComparison(false);
    setSelectedVersion(null);
  };

  // Simple function to strip HTML and get plain text for comparison
  const getPlainText = (html: string | null | undefined) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  };

  // Get current article content for comparison
  const currentContent = articleData?.article ?
    getPlainText(articleData.article.content || articleData.article.content_json) : '';

  const selectedVersionContent = selectedVersion ?
    getPlainText(selectedVersion.content || selectedVersion.content_json) : '';

  // Simple diff highlighting - find differences
  const getDiffHtml = (oldText: string, newText: string) => {
    const oldWords = oldText.split(/(\s+)/);
    const newWords = newText.split(/(\s+)/);

    const result: string[] = [];
    const maxLength = Math.max(oldWords.length, newWords.length);

    for (let i = 0; i < maxLength; i++) {
      const oldWord = oldWords[i] || '';
      const newWord = newWords[i] || '';

      if (oldWord !== newWord) {
        if (oldWord) result.push(`<span style="background-color: #fee; text-decoration: line-through;">${oldWord}</span>`);
        if (newWord) result.push(`<span style="background-color: #efe;">${newWord}</span>`);
      } else {
        result.push(oldWord);
      }
    }

    return result.join('');
  };

  const handleRevert = async () => {
    if (!selectedVersion) return;

    try {
      await revertMutation.mutateAsync({
        articleSlug,
        versionId: selectedVersion.id,
        changeSummary: revertSummary.trim() || `Reverted to version ${selectedVersion.version_number}`
      });

      setRevertDialogOpen(false);
      setRevertSummary("");
      setSelectedVersion(null);
      setOpen(false);
    } catch (error) {
      console.error("Failed to revert version:", error);
    }
  };

  const formatVersionDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      relative: formatDistanceToNow(date, { addSuffix: true }),
      absolute: format(date, "MMM d, yyyy 'at' h:mm a")
    };
  };

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          {trigger || (
            <Button variant="outline" size="sm">
              <History className="h-4 w-4 mr-2" />
              Version History
            </Button>
          )}
        </SheetTrigger>

        <SheetContent className="w-full sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {showDetails && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToList}
                  className="mr-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <History className="h-5 w-5" />
              {showDetails ? `Version ${selectedVersion?.version_number}` : 'Version History'}
            </SheetTitle>
            <SheetDescription>
              {showDetails
                ? `Viewing details for version ${selectedVersion?.version_number}`
                : 'View and revert to previous versions of this article'
              }
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {showDetails && selectedVersion ? (
              // Version Details View
              <div className="space-y-6">
                {/* Version Header */}
                <div className="border-b pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="default">v{selectedVersion.version_number}</Badge>
                      <h3 className="font-semibold text-lg">{selectedVersion.title}</h3>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRevertDialogOpen(true);
                      }}
                      disabled={revertMutation.isPending}
                      className="gap-2"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Revert to this version
                    </Button>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      <span>{selectedVersion.author_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{formatVersionDate(selectedVersion.created_at).absolute}</span>
                    </div>
                  </div>
                  {selectedVersion.change_summary && (
                    <div className="mt-2">
                      <p className="text-sm text-muted-foreground italic">
                        {selectedVersion.change_summary}
                      </p>
                    </div>
                  )}
                </div>

                {/* Version Content Preview or Comparison */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      {showComparison ? 'Changes from Current Version' : 'Content Preview'}
                    </h4>
                    {!showComparison && selectedVersion && articleData?.article && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowComparison(true)}
                        className="gap-2"
                      >
                        <GitCompare className="h-4 w-4" />
                        Show Changes
                      </Button>
                    )}
                    {showComparison && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowComparison(false)}
                        className="gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        Show Content
                      </Button>
                    )}
                  </div>

                  <ScrollArea className="h-[calc(100vh-400px)] border rounded-lg">
                    <div className="p-4">
                      {showComparison && selectedVersion && articleData?.article ? (
                        <div className="space-y-4">
                          <div className="grid gap-4 md:grid-cols-2">
                            <div>
                              <h5 className="font-medium text-sm mb-2 text-muted-foreground">
                                Version {selectedVersion.version_number} (Selected)
                              </h5>
                              <div className="p-3 bg-muted/30 rounded border text-sm">
                                {selectedVersionContent || 'No content'}
                              </div>
                            </div>
                            <div>
                              <h5 className="font-medium text-sm mb-2 text-muted-foreground">
                                Current Version
                              </h5>
                              <div className="p-3 bg-muted/30 rounded border text-sm">
                                {currentContent || 'No content'}
                              </div>
                            </div>
                          </div>
                          <div>
                            <h5 className="font-medium text-sm mb-2 text-muted-foreground">
                              Changes (Highlighted)
                            </h5>
                            <div
                              className="p-3 bg-background rounded border text-sm leading-relaxed"
                              dangerouslySetInnerHTML={{
                                __html: getDiffHtml(selectedVersionContent, currentContent)
                              }}
                            />
                            <p className="text-xs text-muted-foreground mt-2">
                              <span className="inline-block w-3 h-3 bg-red-100 border mr-1"></span>
                              Removed content
                              <span className="inline-block w-3 h-3 bg-green-100 border mr-1 ml-3"></span>
                              Added content
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="prose prose-sm prose-neutral dark:prose-invert max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: selectedVersion?.content || selectedVersion?.content_json || '<p class="text-muted-foreground">No content available for this version</p>'
                          }}
                        />
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            ) : (
              // Version List View
              <>
                {isLoading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-20 bg-muted rounded-lg"></div>
                      </div>
                    ))}
                  </div>
                ) : versions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No Version History</p>
                    <p className="text-sm">
                      Versions will appear here when the article is edited
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-muted-foreground mb-4 space-y-1">
                      <p>Click on any version to view its details and content.</p>
                      <p className="flex items-center gap-2">
                        <Eye className="h-3 w-3" /> View content
                        <GitCompare className="h-3 w-3 ml-2" /> Compare changes
                        <RotateCcw className="h-3 w-3 ml-2" /> Revert version
                      </p>
                    </div>
                    <ScrollArea className="h-[calc(100vh-200px)]">
                      <div className="space-y-3 pr-4">
                        {versions.map((version, index) => {
                          const isLatest = index === 0;
                          const isCurrent = version.version_number === currentVersion;
                          const dateInfo = formatVersionDate(version.created_at);

                          return (
                            <div
                              key={version.id}
                              className={cn(
                                "p-4 border rounded-lg cursor-pointer transition-colors hover:border-primary/50",
                                isCurrent && "border-primary bg-primary/5",
                                selectedVersion?.id === version.id && "border-primary bg-primary/10"
                              )}
                              onClick={() => handleVersionSelect(version)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Badge variant={isLatest ? "default" : "secondary"}>
                                    v{version.version_number}
                                  </Badge>
                                  {isCurrent && (
                                    <Badge variant="outline">Current</Badge>
                                  )}
                                </div>

                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleVersionSelect(version);
                                    }}
                                    title="View version"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  {!isCurrent && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCompareWithCurrent(version);
                                      }}
                                      title="Compare with current version"
                                    >
                                      <GitCompare className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {!isCurrent && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedVersion(version);
                                        setRevertDialogOpen(true);
                                      }}
                                      disabled={revertMutation.isPending}
                                      title="Revert to this version"
                                    >
                                      <RotateCcw className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>

                              <h4 className="font-medium text-sm mb-2 line-clamp-2">
                                {version.title}
                              </h4>

                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  <span>{version.author_name}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  <span title={dateInfo.absolute}>{dateInfo.relative}</span>
                                </div>
                              </div>

                              {version.change_summary && (
                                <p className="text-xs text-muted-foreground mt-2 italic">
                                  {version.change_summary}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Revert Confirmation Dialog */}
      <AlertDialog open={revertDialogOpen} onOpenChange={setRevertDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              Revert to Version {selectedVersion?.version_number}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will restore the article to version {selectedVersion?.version_number}
              created by {selectedVersion?.author_name}{" "}
              {selectedVersion && formatVersionDate(selectedVersion.created_at).relative}.
              The current version will be saved as a new version.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3">
            <Label htmlFor="revert-summary">Change Summary (Optional)</Label>
            <Textarea
              id="revert-summary"
              placeholder="Briefly describe why you're reverting..."
              value={revertSummary}
              onChange={(e) => setRevertSummary(e.target.value)}
              rows={3}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevert}
              disabled={revertMutation.isPending}
              className="bg-warning hover:bg-warning/90"
            >
              {revertMutation.isPending ? "Reverting..." : "Revert Article"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}